// 💎 CLARA INVESTMENT COCKPIT - DASHBOARD KOMPONENTE
// 6 KPI-Kacheln mit Live-Updates und Transparenz-System

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Home, 
  PiggyBank, 
  TrendingUp, 
  Calculator, 
  Gem, 
  DollarSign,
  Info,
  AlertTriangle,
  CheckCircle,
  CreditCard
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CockpitDashboard = ({ kpis, calculationData, selectedProperty, onInfoClick, savedCalculations = [], currentCalculationId, onAnalysisClick }) => {
  // Aktuelle Analyse finden
  const currentAnalysis = savedCalculations.find(calc => calc.id === currentCalculationId);
  
  // KPI-Karten Konfiguration mit Transparenz-Informationen
  const kpiCards = [
    {
      id: 'analysis_selector',
      title: 'Aktuelle Analyse',
      value: currentAnalysis?.calculation_name || 'Keine Analyse geladen',
      format: 'text',
      icon: Calculator,
      color: 'blue',
      subtitle: `${savedCalculations.length} gespeicherte Analysen verfügbar`,
      isClickable: true,
      onClick: onAnalysisClick,
      hasBlueBackground: true, // NEUE EIGENSCHAFT FÜR BLAUEN HINTERGRUND
      info: {
        formula: 'Analyse-Verwaltung',
        explanation: 'Hier können Sie gespeicherte Analysen verwalten, laden und bearbeiten',
        example: 'Klicken Sie um alle gespeicherten Investment-Analysen zu sehen',
        goodValue: 'Organisierte Analyse-Verwaltung'
      }
    },
    {
      id: 'purchase_price',
      title: 'Kaufpreis',
      value: calculationData?.purchase_price || 0,
      format: 'currency',
      icon: Home,
      color: 'blue',
      subtitle: `Inkl. ${calculationData?.purchase_costs_percent || 10}% Nebenkosten • EK: €${(calculationData?.equity || 0).toLocaleString()}`,
      info: {
        formula: 'Kaufpreis + Kaufnebenkosten',
        explanation: 'Der Gesamtinvestitionsbetrag inklusive aller Nebenkosten (Notar, Grunderwerbsteuer, Makler)',
        example: `€${(calculationData?.purchase_price || 0).toLocaleString()} + €${(kpis?.purchaseCostsAbsolute || 0).toLocaleString()} = €${(kpis?.totalInvestment || 0).toLocaleString()}`,
        goodValue: 'Abhängig von Lage und Objekttyp'
      }
    },
    {
      id: 'rental_yield',
      title: 'Mietrendite',
      value: kpis?.grossRentalYield || 0,
      format: 'percent',
      icon: TrendingUp,
      color: getYieldColor(kpis?.grossRentalYield, 6.0, 4.0),
      subtitle: `Netto: ${kpis?.netRentalYield?.toFixed(2) || 0}% • Nach Kosten`,
      info: {
        formula: 'Brutto: (Jahresmiete ÷ Kaufpreis) × 100 | Netto: ((Jahresmiete - Kosten) ÷ Kaufpreis) × 100',
        explanation: 'Bruttomietrendite vor Kosten und Nettomietrendite nach Abzug der Bewirtschaftungskosten',
        example: `Brutto: €${(kpis?.annualRentalIncome || 0).toLocaleString()} ÷ €${(calculationData?.purchase_price || 0).toLocaleString()} = ${kpis?.grossRentalYield?.toFixed(2) || 0}% | Netto: ${kpis?.netRentalYield?.toFixed(2) || 0}%`,
        goodValue: 'Brutto >6% sehr gut, Netto >4% sehr gut'
      }
    },
    {
      id: 'monthly_burden',
      title: 'Monatliche Belastung',
      value: (kpis?.totalMonthlyLoanPayment || 0) + (kpis?.monthlyOperatingCosts || 0),
      format: 'currency',
      icon: CreditCard,
      color: 'orange',
      subtitle: `Darlehen: €${(kpis?.totalMonthlyLoanPayment || 0).toLocaleString()} • Kosten: €${(kpis?.monthlyOperatingCosts || 0).toLocaleString()}`,
      info: {
        formula: 'Monatliche Darlehensrate + Betriebskosten',
        explanation: 'Gesamte monatliche finanzielle Belastung für die Immobilie',
        example: `€${(kpis?.totalMonthlyLoanPayment || 0).toLocaleString()} + €${(kpis?.monthlyOperatingCosts || 0).toLocaleString()} = €${((kpis?.totalMonthlyLoanPayment || 0) + (kpis?.monthlyOperatingCosts || 0)).toLocaleString()}`,
        goodValue: 'Sollte durch Mieteinnahmen gedeckt sein'
      }
    },
    {
      id: 'equity_return',
      title: 'Eigenkapitalrendite',
      value: kpis?.equityReturn || 0,
      format: 'percent',
      icon: Gem,
      color: getYieldColor(kpis?.equityReturn, 8.0, 5.0),
      info: {
        formula: '(Cashflow nach Steuern × 12 ÷ Eigenkapital) × 100',
        explanation: 'Rendite auf Ihr eingesetztes Eigenkapital nach allen Kosten und Steuern',
        example: `€${(kpis?.annualCashflowAfterTax || 0).toLocaleString()} ÷ €${(calculationData?.equity || 0).toLocaleString()} × 100 = ${kpis?.equityReturn?.toFixed(2) || 0}%`,
        goodValue: '>8% sehr gut, 5-8% gut, <5% niedrig'
      }
    },
    {
      id: 'cashflow',
      title: 'Cashflow/Monat',
      value: kpis?.monthlyCashflowAfterTax || 0,
      format: 'currency',
      icon: kpis?.isPositiveCashflow ? TrendingUp : AlertTriangle,
      color: kpis?.isPositiveCashflow ? 'green' : 'red',
      subtitle: kpis?.isPositiveCashflow ? '✅ Positiv' : '⚠️ Negativ',
      info: {
        formula: 'Miete - Kosten - Finanzierung - Steuern',
        explanation: 'Monatlicher Geldfluss nach allen Abzügen',
        example: `€${(kpis?.monthlyTotalIncome || 0).toLocaleString()} - €${(kpis?.monthlyTotalCosts || 0).toLocaleString()} - €${(kpis?.totalMonthlyLoanPayment || 0).toLocaleString()} - €${((kpis?.taxEffect || 0) / 12).toFixed(0)} = €${(kpis?.monthlyCashflowAfterTax || 0).toLocaleString()}`,
        goodValue: 'Positiv für selbsttragendes Investment'
      }
    }
  ];

  // Formatierungsfunktion für Werte
  const formatValue = (value, format) => {
    if (!value && value !== 0) return '—';
    
    switch (format) {
      case 'currency':
        return `€${Math.abs(value).toLocaleString()}`;
      case 'percent':
        return `${value.toFixed(2)}%`;
      case 'text':
        return value;
      default:
        return value.toLocaleString();
    }
  };

  // Info-Modal-Handler
  const handleInfoClick = (kpiInfo) => {
    if (onInfoClick) {
      onInfoClick(kpiInfo);
    }
  };

  return (
    <div className="space-y-6">
      {/* Objekt-Header */}
      {selectedProperty && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Home className="h-6 w-6 text-blue-600" />
                  {selectedProperty.property_name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {selectedProperty.address}, {selectedProperty.postal_code} {selectedProperty.city}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Baujahr</p>
                <p className="text-lg font-semibold">{selectedProperty.construction_year || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI-Grid (3x2) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          const colorClasses = {
            blue: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20',
            green: 'text-green-600 bg-green-100 dark:bg-green-900/20',
            emerald: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/20',
            purple: 'text-purple-600 bg-purple-100 dark:bg-purple-900/20',
            red: 'text-red-600 bg-red-100 dark:bg-red-900/20',
            yellow: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20',
            orange: 'text-orange-600 bg-orange-100 dark:bg-orange-900/20'
          };

          return (
            <Card 
              key={kpi.id} 
              className={`transition-all duration-200 ${
                kpi.isClickable 
                  ? 'cursor-pointer border-2 hover:border-blue-300' 
                  : 'hover:shadow-lg'
              } ${
                kpi.hasBlueBackground 
                  ? 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-700' 
                  : ''
              }`}
              onClick={kpi.isClickable ? kpi.onClick : undefined}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {kpi.title}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInfoClick(kpi.info);
                        }}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                        title="Berechnungsdetails anzeigen"
                      >
                        <Info className="h-4 w-4 text-gray-400 hover:text-blue-600" />
                      </button>
                    </div>
                    
                    <p className={`text-2xl font-bold ${colorClasses[kpi.color]?.split(' ')[0] || 'text-gray-900'} ${
                      kpi.format === 'text' ? 'text-lg' : ''
                    }`}>
                      {formatValue(kpi.value, kpi.format)}
                    </p>
                    
                    {kpi.subtitle && (
                      <p className="text-xs text-gray-500 mt-1">
                        {kpi.subtitle}
                      </p>
                    )}
                  </div>
                  
                  <div className={`p-3 rounded-full ${colorClasses[kpi.color] || 'text-gray-600 bg-gray-100'}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cashflow-Entwicklung */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Cashflow-Entwicklung (10 Jahre)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={kpis?.cashflowProjection || []}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="year" 
                  className="text-xs"
                />
                <YAxis 
                  className="text-xs"
                  tickFormatter={(value) => `€${value}`}
                />
                <Tooltip 
                  formatter={(value) => [`€${value.toLocaleString()}`, 'Monatlicher Cashflow']}
                  labelFormatter={(year) => `Jahr ${year}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="cashflow" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Kostenstruktur */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Monatliche Kostenstruktur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={kpis?.costBreakdown || []}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="category" 
                  className="text-xs"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  className="text-xs"
                  tickFormatter={(value) => `€${value}`}
                />
                <Tooltip 
                  formatter={(value) => [`€${value.toLocaleString()}`, 'Kosten']}
                />
                <Bar 
                  dataKey="amount" 
                  fill="#10B981"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Schnellaktionen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-purple-600" />
            Schnellaktionen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <button className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors flex items-center gap-2">
              📊 Szenario-Vergleich
            </button>
            <button className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors flex items-center gap-2">
              💰 Fördermittel prüfen
            </button>
            <button className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors flex items-center gap-2">
              🏗️ Baurecht analysieren
            </button>
            <button className="px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg transition-colors flex items-center gap-2">
              📍 Lage bewerten
            </button>
            <button className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors flex items-center gap-2">
              🤖 KI-Empfehlungen
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Performance-Zusammenfassung */}
      {kpis && (
        <Card className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Investment-Score</p>
                <p className="text-2xl font-bold text-blue-600">
                  {calculateInvestmentScore(kpis)}/100
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Kaufpreisfaktor</p>
                <p className="text-2xl font-bold text-purple-600">
                  {kpis.purchasePriceMultiplier?.toFixed(1) || '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Miete/m²</p>
                <p className="text-2xl font-bold text-green-600">
                  €{kpis.rentPerSqm?.toFixed(2) || '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Kaufpreis/m²</p>
                <p className="text-2xl font-bold text-orange-600">
                  €{kpis.purchasePricePerSqm?.toFixed(0) || '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Hilfsfunktionen
function getYieldColor(value, goodThreshold, okThreshold) {
  if (!value) return 'gray';
  if (value >= goodThreshold) return 'green';
  if (value >= okThreshold) return 'yellow';
  return 'red';
}

function calculateInvestmentScore(kpis) {
  if (!kpis) return 0;
  
  let score = 0;
  
  // Bruttomietrendite (30 Punkte)
  if (kpis.grossRentalYield >= 8) score += 30;
  else if (kpis.grossRentalYield >= 6) score += 25;
  else if (kpis.grossRentalYield >= 4) score += 15;
  else score += 5;
  
  // Eigenkapitalrendite (30 Punkte)
  if (kpis.equityReturn >= 12) score += 30;
  else if (kpis.equityReturn >= 8) score += 25;
  else if (kpis.equityReturn >= 5) score += 15;
  else score += 5;
  
  // Cashflow (25 Punkte)
  if (kpis.monthlyCashflowAfterTax >= 500) score += 25;
  else if (kpis.monthlyCashflowAfterTax >= 200) score += 20;
  else if (kpis.monthlyCashflowAfterTax >= 0) score += 15;
  else score += 0;
  
  // Kaufpreisfaktor (15 Punkte)
  if (kpis.purchasePriceMultiplier <= 15) score += 15;
  else if (kpis.purchasePriceMultiplier <= 20) score += 10;
  else if (kpis.purchasePriceMultiplier <= 25) score += 5;
  else score += 0;
  
  return Math.min(100, score);
}

export default CockpitDashboard;

